import { flexRender, Table as TanstackTable } from "@tanstack/react-table";

import { useIsMobile } from "@/common/hooks/use-media-query";

type TableMeta =
  | {
      label: () => React.ReactNode;
    }
  | undefined;

const Table = ({ children }: React.PropsWithChildren) => (
  <table className="w-full table-auto text-xs text-can-midnight-steel sm:text-base md:table">
    {children}
  </table>
);

const Head = <T,>({
  table,
  filters,
}: {
  table: TanstackTable<T>;
  filters?: Record<string, React.ReactNode>;
}) => {
  const isMobile = useIsMobile();

  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr className="md:table-row" key={headerGroup.id}>
          {headerGroup.headers
            .filter((header) =>
              isMobile ? header.column.getCanFilter() : true,
            )
            .map((header) => (
              <th
                className="block px-4 py-2 text-left align-top md:table-cell"
                key={header.id}
              >
                <div className="flex items-center justify-between md:block">
                  <div>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                  {header.column.getCanFilter() && filters
                    ? filters[header.column.id]
                    : null}
                </div>
              </th>
            ))}
        </tr>
      ))}
    </thead>
  );
};

const Body = <T,>({ table }: { table: TanstackTable<T> }) => (
  <tbody className="md:table-row-group">
    {table.getRowModel().rows.map((row) => (
      <tr
        className="my-4 block even:bg-can-silver-gray md:mb-0 md:table-row"
        key={row.id}
      >
        {row.getVisibleCells().map((cell) => (
          <td
            className="flex break-words px-4 py-2 md:table-cell"
            key={cell.id}
          >
            <span className="md:hidden">
              {(cell.column.columnDef.meta as TableMeta)?.label()}
            </span>
            <span className="ml-auto md:ml-0">
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </span>
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

Table.Head = Head;
Table.Body = Body;

export { Table };
